<script>
export const addDays = (date, days) => {
  const result = new Date(date)

  result.setDate(result.getDate() + days)

  return result
}

export const addMonths = (date, months) => {
  const result = new Date(date)

  result.setMonth(result.getMonth() + months)

  return result
}

export const customDateFormat = date => {
  return `${date.getFullYear()}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`
}

export const dateRangeConfig = () => {
  return {
    mode: "range",
    maxDate: new Date(),
    dateFormat: "Y-m-d",
    enableTime: false,
    enableSeconds: false,
    noCalendar: false,
    altInput: true,
    altFormat: "F j, Y",
    altInputClass: "form-control",
    altInputPlaceholder: "Select date",
    inline: false,
    static: false,
    appendTo: document.body,
    clickOpens: true,
    defaultDate: [new Date(), new Date()],
  }
}

const GRAPH_COMPATIBLE_TYPES = ['select', 'radio', 'radio_rating', 'star_rating', 'smile', 'scale']
</script>

<script setup>
import { startOfMonth, endOfMonth } from '@/@core/utils/formatters'
import { cookieRef } from '@/@layouts/stores/config'
import HorizontalStackBar from '@/@core/components/graph/HorizontalStackBar.vue'
import HorizontalBar from '@/@core/components/graph/HorizontalBar.vue'
import VerticalBar from '@/@core/components/graph/VerticalBar.vue'
import FeedbackTable from '@/views/feedback/feedback-panel/FeedbackTable.vue'
import { $api } from '@/utils/api'
import { VSkeletonLoader } from 'vuetify/lib/labs/components.mjs'

definePage({
  meta: {
    name: 'dashboard-question',
    subject: 'feedback.menu',
    action: 'dashboard_question',
  },
})

// Unit from cookie
const unitId = computed(() => cookieRef('selectedUnit').value ?? null)

// Form Types
const { data: formTypesData } = await useApi(
  createUrl('/masters/form-types', {
    query: {
      select: ['id', 'name'],
    },
  }),
)

const formTypes = computed(() => formTypesData.value?.data ?? formTypesData.value ?? [])

const selectedFormType = ref(null)

// Date Range
const quickDateRange = ref('Last 30 days')

const quickDateRangeOptions = [
  {
    title: 'Today',
    value: `${customDateFormat(new Date())}`,
  },
  {
    title: 'Yesterday',
    value: `${customDateFormat(addDays(new Date(), -1))}`,
  },
  {
    title: 'Last 7 days',
    value: `${customDateFormat(addDays(new Date(), -6))} to ${customDateFormat(new Date())}`,
  },
  {
    title: 'Last 30 days',
    value: `${customDateFormat(addDays(new Date(), -30))} to ${customDateFormat(new Date())}`,
  },
  {
    title: 'This month',
    value: `${customDateFormat(startOfMonth(new Date()))} to ${customDateFormat(new Date())}`,
  },
  {
    title: 'Last month',
    value: `${customDateFormat(startOfMonth(addMonths(new Date(), -1)))} to ${customDateFormat(endOfMonth(addMonths(new Date(), -1)))}`,
  },
  {
    title: 'Custom',
    value: null,
  },
]

const fromDate = ref(customDateFormat(addDays(new Date(), -30)))
const toDate = ref(customDateFormat(new Date()))

const setQuickDateRange = value => {
  dateRange.value = value?.value
}

const dateRange = ref()

const dateRangeOptions = ref({
  ...dateRangeConfig(),
  onChange: (selectedDates, dateStr) => {
    fromDate.value = customDateFormat(selectedDates[0])
    if (selectedDates[1]) toDate.value = customDateFormat(selectedDates[1])
    else {
      toDate.value = customDateFormat(selectedDates[0])
    }
    dateRange.value = dateStr
  },
  defaultDate: [
    customDateFormat(addDays(new Date(), -30)),
    customDateFormat(new Date()),
  ],
})

watch(dateRange, value => {
  if (value) {
    quickDateRange.value = quickDateRangeOptions.find(option => option.value === value)?.title || 'Custom'
  }
}, { immediate: true })

const dateRangeValue = computed(() => {
  return [fromDate.value, toDate.value]
})

// Score Cards (static values for now)
const scoreCards = computed(() => [
  {
    title: 'Total Feedback',
    icon: 'tabler-clipboard-text',
    color: 'primary',
    value: 0,
  },
  {
    title: 'NPS Score',
    icon: 'tabler-chart-arrows-vertical',
    color: 'info',
    value: 0,
  },
  {
    title: 'P-SAT Score',
    icon: 'tabler-chart-line',
    color: 'success',
    value: 0,
  },
  {
    title: 'Experience Score',
    icon: 'tabler-mood-happy',
    color: 'warning',
    value: 0,
  },
])

// Question Analytics
// Shared by the charts and the export so the two can never filter differently.
const analyticsSearch = computed(() => ({
  form_type_id: selectedFormType.value,
  status: 1,
  created_at: dateRangeValue.value,
  unit_id: unitId.value,
}))

const { isFetching: isLoadingQuestions, data: questionGroupsData } = useApi(
  createUrl('/dashboard/questions/analytics', {
    query: {
      search: analyticsSearch,
      response_analytics: true,
    },
  }),
  {
    beforeFetch: ({ cancel }) => {
      if (!selectedFormType.value) cancel()
    },
  },
)

// Process question groups for display
const processedGroups = computed(() => {
  if (!questionGroupsData.value) return []

  const groups = Array.isArray(questionGroupsData.value)
    ? questionGroupsData.value
    : []

  return groups
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(group => {
      const questions = (group.questions ?? [])
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .filter(question => {
          const hasGraphData = GRAPH_COMPATIBLE_TYPES.includes(question.question_type) && question.question_options?.length > 0

          const hasGraphSubQuestions = (question.sub_questions ?? []).some(
            sub => GRAPH_COMPATIBLE_TYPES.includes(sub.question_type) && sub.question_options?.length > 0,
          )

          return hasGraphData || hasGraphSubQuestions
        })
        .map(question => ({
          ...question,
          graph_data: GRAPH_COMPATIBLE_TYPES.includes(question.question_type) && question.question_options?.length > 0
            ? buildGraphData(question)
            : null,
          processedSubQuestions: (question.sub_questions ?? [])
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .filter(sub => GRAPH_COMPATIBLE_TYPES.includes(sub.question_type) && sub.question_options?.length > 0)
            .map(sub => ({
              ...sub,
              graph_data: buildGraphData(sub),
            })),
        }))

      return {
        ...group,
        processedQuestions: questions,
      }
    })
    .filter(group => group.processedQuestions.length > 0)
})

const hasQuestions = computed(() => processedGroups.value.length > 0)

// Collapsible state for question cards
const expandedQuestions = ref(new Set())

const toggleQuestion = questionId => {
  if (expandedQuestions.value.has(questionId)) {
    expandedQuestions.value.delete(questionId)
  } else {
    expandedQuestions.value.add(questionId)
  }
}

const isQuestionExpanded = questionId => expandedQuestions.value.has(questionId)

// Expand all parent questions by default when data loads
watch(processedGroups, groups => {
  const newExpanded = new Set()

  groups.forEach(group => {
    group.processedQuestions.forEach(question => {
      if (question.total_responses > 0) {
        newExpanded.add(question.id)
      }
    })
  })
  expandedQuestions.value = newExpanded
})

// Build graph data from question_options
function buildGraphData(question) {
  const options = question.question_options
  if (!options?.length) return []

  if (question.question_type === 'scale') {
    const scaleColors = colorPaletteForNPS()

    return options.map((option, index) => ({
      question_option_id: option.id,
      label: option.label,
      value: option.value,
      count: option.response_count ?? 0,
      percentage: option.percentage ?? 0,
      color: scaleColors[index] ?? scaleColors[scaleColors.length - 1],
    }))
  }

  const useRatingColors = question.question_type === 'radio_rating' || question.question_type === 'smile'

  return options.map(option => ({
    question_option_id: option.id,
    label: option.label,
    value: option.value,
    count: option.response_count ?? 0,
    percentage: option.percentage ?? 0,
    color: useRatingColors ? radioRatingColor(option.value) : (option.color ?? null),
  }))
}

// Build NPS bar data for scale questions
function buildNpsGraphData(nps) {
  if (!nps) return null

  return [
    {
      label: 'Detractors (0-6)',
      count: nps.detractors?.count ?? 0,
      percentage: nps.detractors?.percentage ?? 0,
      color: '#FF5252',
    },
    {
      label: 'Passives (7-8)',
      count: nps.passives?.count ?? 0,
      percentage: nps.passives?.percentage ?? 0,
      color: '#FFB400',
    },
    {
      label: 'Promoters (9-10)',
      count: nps.promoters?.count ?? 0,
      percentage: nps.promoters?.percentage ?? 0,
      color: '#43A047',
    },
  ]
}

// Determine graph type based on options count and question type
function getGraphType(question) {
  const optionCount = question.question_options?.length ?? 0
  const type = question.question_type

  if (type === 'smile') {
    return 'horizontal-stack'
  }

  if (optionCount >= 9 || type === 'scale' || type === 'radio_rating' || type === 'star_rating') {
    return 'vertical'
  }

  if (optionCount <= 3) {
    return 'horizontal-stack'
  }

  return 'horizontal'
}

// Export
const toast = useToast()
const isExporting = ref(false)

const exportAnalytics = async () => {
  isExporting.value = true

  try {
    // Progress, download and failure toasts are handled globally by ExportSnackbar,
    // which listens on the export-status channel.
    await $api(createUrl('/dashboard/questions/analytics/export', {
      query: { search: analyticsSearch },
    }).value)
  } catch (error) {
    // api.js sets `reported` when its global handler already raised a toast for
    // this response, and leaves it false on a 401 — which it handles by silently
    // redirecting to login. Toast here only for what the user has not been told.
    if (!error?.reported && error?.status !== 401) {
      toast.error('Failed to start export', 'error')
    }
  } finally {
    isExporting.value = false
  }
}

// Drill-Down Dialog
const isFeedbackDialogVisible = ref(false)
const feedbackTableFilter = ref(null)

const handleDataPointClick = data => {
  feedbackTableFilter.value = {
    created_at: [fromDate.value, toDate.value],
    unit_id: unitId.value,
    form_type_id: selectedFormType.value,
    'feedback_responses.question_option_id': data.question_option_id,
  }
  isFeedbackDialogVisible.value = true
}

watch(() => isFeedbackDialogVisible.value, value => {
  if (value === false) feedbackTableFilter.value = null
})
</script>

<template>
  <div>
    <!-- Page Title -->
    <!--
      <VRow>
      <VCol cols="12">
      <span class="text-h3 font-weight-bold text-primary text-capitalize">
      Form Type Analytics
      </span>
      </VCol>
      </VRow> 
    -->

    <!-- Filters - mb-4 -->
    <VRow class="mb-2">
      <VCol cols="12">
        <VCard
          variant="flat"
          class="border"
        >
          <VCardText>
            <div class="d-flex flex-wrap align-end gap-4">
              <div class="filter-field">
                <AppSelect
                  v-model="selectedFormType"
                  label="Form Type"
                  placeholder="Select Form Type"
                  :items="formTypes"
                  :item-title="item => item.name"
                  :item-value="item => item.id"
                  clearable
                />
              </div>
              <div class="filter-field">
                <AppSelect
                  v-model="quickDateRange"
                  label="Quick Range"
                  return-object
                  :item-title="item => item.title"
                  :item-value="item => item.title"
                  :items="quickDateRangeOptions"
                  @update:model-value="setQuickDateRange"
                />
              </div>
              <div class="filter-field">
                <AppDateTimePicker
                  v-model="dateRange"
                  label="Range"
                  placeholder="Select date"
                  :config="dateRangeOptions"
                />
              </div>
              <VBtn
                variant="flat"
                prepend-icon="tabler-download"
                :loading="isExporting"
                :disabled="!selectedFormType || isLoadingQuestions"
                @click="exportAnalytics"
              >
                Export
              </VBtn>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Content: Only show when form type is selected -->
    <template v-if="selectedFormType">
      <!-- Score Cards -->
      <!--
        <VRow class="py-4">
        <VCol
        v-for="{ title, value, icon, color } in scoreCards"
        :key="title"
        cols="12"
        md="3"
        sm="6"
        >
        <div class="d-flex align-center">
        <VAvatar
        variant="tonal"
        :color="color"
        rounded
        size="54"
        class="me-4"
        >
        <VIcon
        :icon="icon"
        size="38"
        />
        </VAvatar>
        <div>
        <h6 class="text-h6 text-medium-emphasis">
        {{ title }}
        </h6>
        <h4
        class="text-h4"
        :class="`text-${color}`"
        >
        {{ value }}
        </h4>
        </div>
        </div>
        </VCol>
        </VRow>

        <VDivider class="my-4" /> 
      -->

      <!-- Loading State -->
      <VRow v-if="isLoadingQuestions">
        <VCol cols="12">
          <VSkeletonLoader type="card" />
        </VCol>
        <VCol cols="12">
          <VSkeletonLoader type="card" />
        </VCol>
      </VRow>

      <!-- Question Analytics -->
      <div
        v-else-if="hasQuestions"
        class="d-flex flex-column gap-6"
      >
        <div
          v-for="group in processedGroups"
          :key="group.id"
        >
          <!-- Group Header -->
          <div class="section-header d-flex align-center gap-2 pa-3 rounded-lg mb-4">
            <div class="section-indicator" />
            <span class="text-subtitle-1 font-weight-medium">
              {{ group.name || group.title }}
            </span>
          </div>

          <!-- Questions in Group -->
          <div class="d-flex flex-column gap-4">
            <div
              v-for="question in group.processedQuestions"
              :key="question.id"
            >
              <!-- Parent Question Card -->
              <VCard
                v-if="question.graph_data"
                variant="flat"
                class="border"
              >
                <VCardItem
                  class="cursor-pointer"
                  @click="toggleQuestion(question.id)"
                >
                  <VCardTitle
                    class="text-h6"
                    style="white-space: normal;"
                  >
                    {{ question.label }}
                  </VCardTitle>
                  <template #append>
                    <div class="d-flex align-center gap-2">
                      <VChip
                        v-if="question.experience?.overall_rating != null"
                        size="small"
                        :color="question.experience?.overall_rating >= 4 ? 'success' : question.experience?.overall_rating >= 2.5 ? 'warning' : 'error'"
                        variant="tonal"
                      >
                        Rating: {{ question.experience.overall_rating }}/5
                      </VChip>
                      <VChip
                        v-if="question.experience?.overall_experience != null"
                        size="small"
                        :color="question.experience?.overall_experience >= 80 ? 'success' : question.experience?.overall_experience >= 50 ? 'warning' : 'error'"
                        variant="tonal"
                      >
                        Experience: {{ question.experience.overall_experience }}%
                      </VChip>
                      <VChip
                        v-if="question.nps"
                        size="small"
                        :color="question.nps.score >= 80 ? 'success' : question.nps.score >= 50 ? 'warning' : 'error'"
                        variant="tonal"
                      >
                        NPS Score: {{ question.nps.score }}
                      </VChip>
                      <VChip
                        size="small"
                        color="primary"
                        variant="tonal"
                      >
                        Responses: {{ question.total_responses ?? 0 }}
                      </VChip>
                      <VIcon
                        :icon="isQuestionExpanded(question.id) ? 'tabler-chevron-up' : 'tabler-chevron-down'"
                        size="20"
                      />
                    </div>
                  </template>
                </VCardItem>
                <template v-if="isQuestionExpanded(question.id)">
                  <VDivider />
                  <VCardText>
                    <HorizontalStackBar
                      v-if="getGraphType(question) === 'horizontal-stack'"
                      :graph-data="question.graph_data"
                      @on-click-data-point="handleDataPointClick"
                    />
                    <HorizontalBar
                      v-else-if="getGraphType(question) === 'horizontal'"
                      :graph-data="question.graph_data"
                      @on-click-data-point="handleDataPointClick"
                    />
                    <VerticalBar
                      v-else
                      :graph-data="question.graph_data"
                      show-data-labels
                      @on-click-data-point="handleDataPointClick"
                    />
                    <!-- :show-data-labels="question.question_type === 'scale'" -->
                    <!-- :show-legend-count="question.question_type !== 'scale'" -->
                  </VCardText>

                  <!-- NPS Breakdown for scale questions -->
                  <template v-if="question.nps">
                    <VDivider />
                    <VCardText>
                      <!--
                        <div class="d-flex align-center justify-space-between mb-3">
                        <span class="text-subtitle-1 font-weight-medium">
                        NPS Breakdown
                        </span>
                        <VChip
                        size="small"
                        :color="question.nps.score >= 50 ? 'success' : question.nps.score >= 0 ? 'warning' : 'error'"
                        variant="tonal"
                        >
                        NPS Score: {{ question.nps.score }}
                        </VChip>
                        </div> 
                      -->
                      <HorizontalStackBar :graph-data="buildNpsGraphData(question.nps)" />
                    </VCardText>
                  </template>
                </template>
              </VCard>

              <!-- Sub-Questions (VExpansionPanels for animation) -->
              <!--
                <VExpansionPanels
                v-if="question.processedSubQuestions.length > 0"
                class="sub-questions-container sub-questions-expansion mt-2"
                >
                <VExpansionPanel class="sub-questions-expansion">
                <VExpansionPanelTitle>
                <span class="text-body-1 font-weight-medium text-primary">
                {{ question.processedSubQuestions.length }} Sub-Question{{ question.processedSubQuestions.length > 1 ? 's' : '' }}
                </span>
                </VExpansionPanelTitle>
                <VExpansionPanelText>
                <div class="d-flex flex-column gap-4">
                <VCard
                v-for="sub in question.processedSubQuestions"
                :key="sub.id"
                variant="flat"
                class="border"
                >
                <VCardItem
                class="cursor-pointer"
                @click="toggleQuestion(sub.id)"
                >
                <VCardTitle
                class="text-h6"
                style="white-space: normal;"
                >
                <VChip
                size="x-small"
                color="secondary"
                class="me-2"
                >
                Sub
                </VChip>
                {{ sub.label }}
                </VCardTitle>
                <template #append>
                <div class="d-flex align-center gap-2">
                <VChip
                v-if="sub.total_responses"
                size="small"
                color="primary"
                variant="tonal"
                >
                {{ sub.total_responses }} responses
                </VChip>
                <VIcon
                :icon="isQuestionExpanded(sub.id) ? 'tabler-chevron-up' : 'tabler-chevron-down'"
                size="20"
                />
                </div>
                </template>
                </VCardItem>
                <template v-if="isQuestionExpanded(sub.id)">
                <VDivider />
                <VCardText>
                <HorizontalStackBar
                v-if="getGraphType(sub) === 'horizontal-stack'"
                :graph-data="sub.graph_data"
                @on-click-data-point="handleDataPointClick"
                />
                <HorizontalBar
                v-else-if="getGraphType(sub) === 'horizontal'"
                :graph-data="sub.graph_data"
                @on-click-data-point="handleDataPointClick"
                />
                <VerticalBar
                v-else
                :graph-data="sub.graph_data"
                @on-click-data-point="handleDataPointClick"
                />
                </VCardText>
                </template>
                </VCard>
                </div>
                </VExpansionPanelText>
                </VExpansionPanel>
                </VExpansionPanels> 
              -->

              <div
                v-if="question.processedSubQuestions.length > 0"
                class="sub-questions-container mt-4 d-flex flex-column gap-4"
              >
                <VCard
                  v-for="sub in question.processedSubQuestions"
                  :key="sub.id"
                  variant="flat"
                  class="border"
                >
                  <VCardItem
                    class="cursor-pointer"
                    @click="toggleQuestion(sub.id)"
                  >
                    <VCardTitle
                      class="text-h6"
                      style="white-space: normal;"
                    >
                      <!--
                        <VChip
                        size="x-small"
                        color="secondary"
                        class="me-2"
                        >
                        Sub
                        </VChip> 
                      -->
                      {{ sub.label }}
                    </VCardTitle>
                    <template #append>
                      <div class="d-flex align-center gap-2">
                        <VChip
                          v-if="sub.experience?.overall_rating != null"
                          size="small"
                          :color="sub.experience?.overall_rating >= 4 ? 'success' : sub.experience?.overall_rating >= 2.5 ? 'warning' : 'error'"
                          variant="tonal"
                        >
                          Rating: {{ sub.experience.overall_rating }}/5
                        </VChip>
                        <VChip
                          v-if="sub.experience?.overall_experience != null"
                          size="small"
                          :color="sub.experience?.overall_experience >= 80 ? 'success' : sub.experience?.overall_experience >= 50 ? 'warning' : 'error'"
                          variant="tonal"
                        >
                          Experience: {{ sub.experience.overall_experience }}%
                        </VChip>
                        <VChip
                          v-if="sub.total_responses"
                          size="small"
                          color="primary"
                          variant="tonal"
                        >
                          Responses: {{ sub.total_responses }}
                        </VChip>
                        <VIcon
                          :icon="isQuestionExpanded(sub.id) ? 'tabler-chevron-up' : 'tabler-chevron-down'"
                          size="20"
                        />
                      </div>
                    </template>
                  </VCardItem>
                  <template v-if="isQuestionExpanded(sub.id)">
                    <VDivider />
                    <VCardText>
                      <HorizontalStackBar
                        v-if="getGraphType(sub) === 'horizontal-stack'"
                        :graph-data="sub.graph_data"
                        @on-click-data-point="handleDataPointClick"
                      />
                      <HorizontalBar
                        v-else-if="getGraphType(sub) === 'horizontal'"
                        :graph-data="sub.graph_data"
                        @on-click-data-point="handleDataPointClick"
                      />
                      <VerticalBar
                        v-else
                        :graph-data="sub.graph_data"
                        show-data-labels
                        @on-click-data-point="handleDataPointClick"
                      />
                      <!-- :show-data-labels="sub.question_type === 'scale'" -->
                      <!-- :show-legend-count="sub.question_type !== 'scale'" -->
                    </VCardText>
                  </template>
                </VCard>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <VRow v-else>
        <VCol cols="12">
          <VCard
            variant="flat"
            class="border"
          >
            <VCardText>
              <p class="text-center text-medium-emphasis mb-0">
                No analytics data available for this form type
              </p>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </template>

    <!-- Empty State: No Form Type Selected -->
    <template v-else>
      <VRow>
        <VCol cols="12">
          <VCard
            variant="flat"
            class="border"
          >
            <VCardText class="text-center pa-8">
              <VAvatar
                color="primary"
                variant="tonal"
                size="64"
                class="mb-4"
              >
                <VIcon
                  icon="tabler-chart-bar"
                  size="36"
                />
              </VAvatar>
              <h6 class="text-h6 mb-2">
                Select a Form Type
              </h6>
              <p class="text-body-2 text-medium-emphasis mb-0">
                Choose a form type from the filter above to view analytics
              </p>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </template>

    <!-- Drill-Down Dialog -->
    <VDialog v-model="isFeedbackDialogVisible">
      <DialogCloseBtn
        size="small"
        @click="() => (isFeedbackDialogVisible = !isFeedbackDialogVisible)"
      />

      <VCard>
        <VCardText class="pa-0">
          <FeedbackTable
            :feedback-filters="feedbackTableFilter"
            :action-visible="false"
            :style="{
              cardVariant: 'flat',
            }"
          />
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>

<style lang="scss" scoped>
.filter-field {
  // Share whatever width the Export button leaves, so the row fills the card
  // instead of ending in dead space, and wrap onto a new line rather than
  // squeezing below a readable width.
  flex: 1 1 200px;
  min-inline-size: 200px;
}

.section-header {
  background: rgba(var(--v-theme-primary), 0.08);
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.section-indicator {
  width: 4px;
  height: 24px;
  background: rgb(var(--v-theme-primary));
  border-radius: 2px;
}

.sub-questions-container {
  border-inline-start: 6px solid rgba(var(--v-theme-primary), 0.3);
  border-radius: 4px;
  padding-inline-start: 16px;
}

.sub-questions-expansion {
  :deep(.v-expansion-panel) {
    background: transparent;
    box-shadow: none;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 6px;
  }

  :deep(.v-expansion-panel-title) {
    min-height: 48px;
  }
}
</style>
